import * as Actions from '../actions/roleActions';
import Role from '../../entities/role';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type RoleAction = { 
    type: string; 
    role: Role | null; 
    roleId: string | Role; 
    partial: boolean; 
    lastFetch: number; 
    roles: Role[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Role> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Role[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Role> | null, firmId: string, userId: string, updates?: Role[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'role', 'roles', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Role>} data
 * @param {Role[]} [updates]
 * @returns {ArrayMap<Role>}
 */
function updateStorage(data: ArrayMap<Role>, updates?: Role[]): ArrayMap<Role> {
	if (logging) console.debug('⮂ roleReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Role[]} [updates]
 * @returns {ArrayMap<Role>}
 */
function updateStorageNoReturn(updates?: Role[]): void {
	if (logging) console.debug('⮂ roleReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Role[]} [data]
 * @returns {ArrayMap<Role>}
 */
function createState(data?: Role[] | ArrayMap<Role>): ArrayMap<Role> {
	if (!data) return ArrayMapRegistry.clone<Role>('role', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Role>('role', data.map(e => e));
    else return ArrayMapRegistry.clone<Role>('role', data);
}

function roles(state: ArrayMap<Role>, action: RoleAction): ArrayMap<Role> {
	if (!state) state = createState();

	let st: ArrayMap<Role>;

	switch (action.type) {
		// action.role - the new value of role with id action.id
		// action.roleId - id of the target, used if new value is null
		case Actions.ROLE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ roleReducer: FETCH_ONE_SUCCESS', action);

			if (action.role === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.roleId)) {
				st = state.clone();
				st.set(action.role);
				// state.clear();

				updateStorageNoReturn([action.role]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.roles
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.roles
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.roles
			 !partial, !stateEmpty, fetchEmpty	-> D return roles in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge roles in state newer than those in action.roles
		 */
		case Actions.ROLE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.roles.length === 0;
			const fetched = action.roles.map(role => new Role({...role, persisted: true}));

			if (logging) console.debug(`⮂ roleReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} roles with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ roleReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ roleReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Role> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Role[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(role => {
					const current = st.get(role.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && role.status === 'D') {
							// Remove it from state
							toUpdate.push(role);
							st.delete(role);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (role.addedOn > current.addedOn) {
								toUpdate.push(role);
								st.set(role);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (role.status !== 'D') {
							toUpdate.push(role);
							st.set(role);
						}
						else if (logging) console.debug('⮂ roleReducer: partialMerge: skipping deleted role not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ roleReducer: empty: roles', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ roleReducer: fullMerge: roles', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localRole => {
                    // If this role has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localRole.persisted) {
                        const fetchedRole = st.get(localRole.id);
                        if (!fetchedRole || localRole.addedOn > fetchedRole.addedOn) st.set(localRole);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.ROLE_FETCH_ONE_FAILURE:
			console.error('ROLE_FETCH_ONE_FAILURE', action.roleId, action.err);

			// No update to storage required
			return state;

		case Actions.ROLE_FETCH_FAILURE:
			console.error('ROLE_FETCH_FAILURE', action.roleId, action.err);

			// No update to storage required
			return state;

		case Actions.ROLE_RESTORE: {
		    let role = new Role({...action.role});
            st = state.clone();

            const existing = st.get(role.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (role.addedOn >= existing.addedOn) {
                    st.set(role);
                    return updateStorage(st, [role]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (role.status === 'D') return state;

                st.set(role);
                return updateStorage(st, [role]);
            }
        }
            
		case Actions.ROLE_SAVE: {
		    // console.debug('⮂ ROLE_SAVE reducer', action.role?.id);
		    
			// clone the role
			let role = new Role({...action.role});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new role in the state
			// If this is a create, add to the queue
			const _role = st.get(role.id);

			if (!_role) st.set(role);
			else {
				if (role.addedOn >= _role.addedOn) st.set(role);
			}

			return updateStorage(st, [role]);
		}

		case Actions.ROLE_SAVE_MANY: {
		    if (logging) console.debug('⮂ ROLE_SAVE_MANY reducer', action.roles.map(role => role.id).join(','));
		    
			// clone the roles
			let toProcess = action.roles.map(role => new Role(role));

			st = state.clone();
			// state.clear();

			const updates: Role[] = [];

			toProcess.forEach(role => {
				// Add or update the new role in the state
				// If this is a create, add to the queue
				const existing = st.get(role.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (role.status !== 'D') {
						st.push(role);
						updates.push(role);
					}
					else if (logging) console.debug('⮂ ROLE_SAVE_MANY: skipping deleted role not in state');
				}
				else {
					if (role.addedOn >= existing.addedOn) {
						st.set(role);
						updates.push(role);
					}
					else if (logging) console.debug('⮂ Skipping, existing role had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ROLE_REMOVE: {
			// clone the role
			const role = new Role({...action.role});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(role);

			return updateStorage(st, [role]);
		}

		case Actions.ROLE_RESTORE_FAILURE:
		case Actions.ROLE_SAVE_FAILURE: {
		    console.warn('ROLE_SAVE_FAILURE reducer', action.role?.id);
		    if (!action.role?.id) {
				console.warn('ROLE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const role = ensure(Role, action.role);
			if (!role) {
				console.error('Could not find role after call to save');
				return state;
			}

			st = state.clone();

			const _roleRef = state.get(role.id);

			if (_roleRef && (_roleRef.addedOn === role.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _role = new Role({..._roleRef});
				_role.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_role.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('role', _role).then(r => {
						if (r.isErr()) console.error(`⮂ roleReducer: failed to save role with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ roleReducer: saved role with fatalError info');
					}).catch(err => console.error(`⮂ roleReducer: failed to save role with fatalError info: ${err.message}`));

				st.set(_role);
			}

			return updateStorage(st);
		}

		case Actions.ROLE_SAVE_MANY_FAILURE: {
		    console.warn('ROLE_SAVE_MANY_FAILURE reducer', action.roles.map(role => role.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.ROLE_RESTORE_SUCCESS:
		case Actions.ROLE_SAVE_SUCCESS: {
			if (!action.role?.id) {
				console.warn('ROLE_SAVE_SUCCESS reducer');
                return state;
			}

			const roleRef = state.get(action.role?.id);
			if (!roleRef) {
				console.warn('Could not find role after call to save');
                return state;
			}

			st = state.clone();

			if (action.role.addedOn >= roleRef.addedOn) {
				st.set(action.role);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const role = new Role({...roleRef, persisted: true});
				st.set(role);
			}

			return updateStorage(st, [action.role]);
		}

		case Actions.ROLE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ ROLE_SAVE_MANY_SUCCESS reducer', action.roles.map(role => role.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const roles: Role[] = [];

			action.roles.forEach(e => {
				const roleRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!roleRef) console.warn('Could not find role after call to save');
				else if (e.addedOn >= roleRef.addedOn) {
				    st.set(e);
				    roles.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const role = new Role({...roleRef, persisted: true});
					st.set(role);
					roles.push(role);
				}
			});

			return updateStorage(st, roles);
		}

		case Actions.ROLE_REMOVE_FAILURE: {
		    console.warn('ROLE_REMOVE_FAILURE action', action.role?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.ROLE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.ROLE_RESTORE_MANY: {
			if (logging) console.debug('⮂ ROLE_RESTORE_MANY reducer', action.roles.map(role => role.id).join(','));

			const toProcess = action.roles.map(role => new Role({...role}));
			st = state.clone();

			const updates: Role[] = [];

			toProcess.forEach(role => {
				const existing = st.get(role.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (role.addedOn >= existing.addedOn) {
						st.set(role);
						updates.push(role);
					}
					else if (logging) console.debug('⮂ ROLE_RESTORE_MANY: skipping older version of role');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (role.status !== 'D') {
						st.set(role);
						updates.push(role);
					}
					else if (logging) console.debug('⮂ ROLE_RESTORE_MANY: skipping deleted role not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ROLE_REMOVE_MANY: {
			if (logging) console.debug('⮂ ROLE_REMOVE_MANY reducer', action.roles.map(role => role.id).join(','));

			const toProcess = action.roles.map(role => new Role({...role}));
			st = state.clone();

			toProcess.forEach(role => {
				st.delete(role);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default roles;